import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { PublicPageShell } from "../components/PublicPageShell";
import { StoreFeatureTiles } from "../components/store/StoreSections";
import { useStoreProducts, gbp } from "../lib/store";
import { VAULT } from "../data/vaultStats";

function ProductCard({ p, i }) {
  return (
    <Link to={`/store/${p.slug}`} className="group relative block border border-zinc-900 bg-black hover:border-purple-500/60 transition-colors overflow-hidden" style={{ animationDelay: `${i * 80}ms` }} data-testid={`store-card-${p.slug}`}>
      <div className="relative h-64 bg-[#0a0a0a] flex items-center justify-center overflow-hidden">
        <img src="/store/vault-door.webp" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-30 group-hover:opacity-45 group-hover:scale-105 transition-all duration-700" />
        {p.image && <img src={p.image} alt={`${p.name} box art`} className="relative w-36 drop-shadow-[0_20px_40px_rgba(168,85,247,0.4)] group-hover:-translate-y-1 transition-transform duration-500" />}
      </div>
      <div className="p-6">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-purple-300">Track {p.order}</span>
          <span className="font-mono text-sm text-white">{gbp(p.price)}</span>
        </div>
        <h2 className="mt-2 font-display text-xl font-black tracking-tight text-white">{p.name}</h2>
        <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{p.tagline}</p>
        <div className="mt-5 inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-purple-300 group-hover:gap-3 transition-all">
          {p.owned ? "You own this" : p.purchasable ? "View & unlock" : "View details"} <ArrowRight size={14} />
        </div>
      </div>
    </Link>
  );
}

export default function StorePage() {
  const { products, loading, tagline } = useStoreProducts();
  return (
    <PublicPageShell crumbs={[{ label: "Store" }]} testId="store-page">
      <section className="relative overflow-hidden border border-zinc-900 bg-black" data-testid="store-hero">
        <img src="/store/vault-hero.webp" alt="" aria-hidden="true" className="absolute inset-y-0 right-0 h-full w-auto max-w-none opacity-50 pointer-events-none select-none" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,#000_40%,rgba(0,0,0,0.7)_65%,rgba(0,0,0,0.15)_100%)]" />
        <div className="relative p-6 sm:p-8 md:p-14 max-w-2xl min-w-0">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-purple-300">Stag Vault Store</div>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.98] tracking-tight"><span className="text-white">Open the</span> <span className="text-purple-300">Vault.</span></h1>
          <p className="mt-5 text-base md:text-lg text-zinc-300">{tagline || "AI tools for a bigger tomorrow."} Buy a single track outright, or unlock all {VAULT.tracks} with Stag Vault Complete (Lifetime Access).</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#products" className="inline-flex items-center gap-2 bg-purple-500 hover:bg-purple-400 text-black px-6 py-3 text-xs tracking-[0.25em] uppercase font-bold transition-colors" data-testid="store-shop-link"><Sparkles size={14} /> Shop single tracks</a>
            <Link to="/pricing" className="inline-flex items-center gap-2 border border-purple-500 text-purple-200 hover:bg-purple-500/10 px-6 py-3 text-xs tracking-[0.25em] uppercase font-bold transition-colors" data-testid="store-explore-link">Explore the full vault <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>

      <section id="products" className="mt-14" data-testid="store-products">
        <div className="flex items-end justify-between gap-6 mb-6">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-purple-300">Single tracks</div>
            <h2 className="mt-2 font-display text-2xl sm:text-3xl font-black tracking-tight">Pay once. Keep it.</h2>
          </div>
          <p className="hidden md:block text-sm text-zinc-500 max-w-xs text-right">More tracks are added as single purchases over time. Every one is already included in Lifetime Access.</p>
        </div>
        {loading ? <div className="text-zinc-500 py-10" data-testid="store-loading">Loading…</div> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p, i) => <ProductCard key={p.slug} p={p} i={i} />)}
            <Link to="/pricing" className="group relative block border border-purple-500/40 bg-black hover:border-purple-400 transition-colors overflow-hidden" data-testid="store-lifetime-card">
              <div className="relative h-64 bg-[#0a0a0a] flex items-center justify-center overflow-hidden">
                <img src="/store/vault-door.webp" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-30 group-hover:opacity-45 group-hover:scale-105 transition-all duration-700" />
                <img src="/store/lifetime-box.webp" alt="Stag Vault Complete box art" className="relative h-52 drop-shadow-[0_20px_40px_rgba(168,85,247,0.45)] group-hover:-translate-y-1 transition-transform duration-500" />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-purple-300">Want everything now?</span>
                  <span className="font-mono text-sm text-white">£25</span>
                </div>
                <h2 className="mt-2 font-display text-xl font-black tracking-tight text-white">Stag Vault Complete</h2>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">Lifetime Access — £25</div>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">All {VAULT.tracks} tracks, every future drop, every single-track product included. One payment, no subscription required.</p>
                <div className="mt-5 inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-purple-300 group-hover:gap-3 transition-all">Get Stag Vault Complete <ArrowRight size={14} /></div>
              </div>
            </Link>
          </div>
        )}
      </section>

      <div className="mt-16"><StoreFeatureTiles /></div>
    </PublicPageShell>
  );
}
